import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import MainApp from "./components/MainApp";
import Login from "./components/Login";
import {
  clearAllStorage,
  clearExpiredSession,
  initializeSession,
  validateSession,
  getStoreId as getStoredStoreId,
  setStoreId,
  getEcwidAuth,
  setEcwidAuth,
  setEcwidEmbedContext,
  setApiKey,
  setSettings
} from "./utils/storage";
import i18n, { SUPPORTED_LANGS } from "./i18n";
import * as logger from "./utils/logger";
import "./App.css";

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializingRef = useRef(false);

  useEffect(() => {
    const existingClarity = document.getElementById("clarity-script");
    if (!existingClarity) {
      const clarityScript = document.createElement("script");
      clarityScript.id = "clarity-script";
      clarityScript.type = "text/javascript";
      clarityScript.text = `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "xvy49ny5hs");`;
      document.head.appendChild(clarityScript);
    }

    const existingZohoInline = document.getElementById("zoho-salesiq-inline");
    if (!existingZohoInline) {
      const zohoInline = document.createElement("script");
      zohoInline.id = "zoho-salesiq-inline";
      zohoInline.type = "text/javascript";
      zohoInline.text = "window.$zoho=window.$zoho || {};window.$zoho.salesiq=window.$zoho.salesiq||{ready:function(){}};";
      document.head.appendChild(zohoInline);
    }

    const existingZohoWidget = document.getElementById("zsiqscript");
    if (!existingZohoWidget) {
      const zohoWidget = document.createElement("script");
      zohoWidget.id = "zsiqscript";
      zohoWidget.type = "text/javascript";
      zohoWidget.async = true;
      zohoWidget.src = "https://salesiq.zohopublic.in/widget?wc=siq61e7d4af0c7813450f1b1656d673d5949c4876f3a5515d6a15c753b77d7223e9";
      document.head.appendChild(zohoWidget);
    }
  }, []);  

  // Load API key and settings from backend
  const loadApiKeyAndSettings = async (hashSiteId) => {
    try {
      if (!hashSiteId) return;

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${hashSiteId}`);

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.settings) {
          const backendSettings = settingsData.settings;

          if (backendSettings.apiKey) {
            setApiKey(backendSettings.apiKey);
          }

          setSettings(backendSettings);
        }
      }
    } catch (error) {
      logger.error("Failed to load API key and settings:", error);
    }
  };

  useEffect(() => {
    logger.log("[App] useEffect initialize");
    const initialize = async () => {
      if (initializingRef.current) return;
      logger.log("[App] initialize");
      initializingRef.current = true;
      // Payload from URL (Ecwid passes encrypted payload when opening app in iframe)
      const payloadFromUrl = new URLSearchParams(window.location.search).get("payload");
      logger.log("[App] payload from URL", payloadFromUrl);
      logger.log("[App] payload from URL", payloadFromUrl ? "present" : "absent");

      const id = new URLSearchParams(window.location.search).get("id");
      const storeId = new URLSearchParams(window.location.search).get("store_id");
      const token = new URLSearchParams(window.location.search).get("token");

      const storedHashId = getStoredStoreId();
      const storedAuth = getEcwidAuth();

      // Embedded in Ecwid Control Panel: auth via ?payload= (decrypted on backend) or EcwidApp.getPayload()
      const isEmbedded = window.self !== window.top && typeof window.EcwidApp !== "undefined";
      logger.log("[App] isEmbedded", isEmbedded);
      if (isEmbedded && window.EcwidApp) {
        try {
          const app_state = new URLSearchParams(window.location.search).get("app_state") || undefined;
          let body = {};
          if (app_state) body.app_state = app_state;
          if (payloadFromUrl) {
            body.payload = payloadFromUrl;
          } else {
            const payload = window.EcwidApp.getPayload();
            if (payload?.store_id && payload?.access_token) {
              body.store_id = payload.store_id;
              body.access_token = payload.access_token;
              if (payload.lang != null) body.lang = payload.lang;
              if (payload.view_mode != null) body.view_mode = payload.view_mode;
              if (payload.public_token != null) body.public_token = payload.public_token;
            }
          }
          if ((body.store_id && body.access_token) || body.payload) {
            logger.info("[App] embed-auth start", {
              storeId: body.store_id || "",
              hasPayload: Boolean(body.payload),
            });
            const apiUrl = import.meta.env.VITE_BACKEND_URL;
            const res = await fetch(`${apiUrl}/api/embed-auth`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body)
            });
            if (res.ok) {
              const data = await res.json();
              logger.info("[App] embed-auth response", {
                status: res.status,
                hashSiteId: data.hashSiteId || "",
                storeId: data.store_id || "",
              });
              if (data.hashSiteId && data.store_id && data.access_token) {
                const token = data.access_token;
                const storeId = data.store_id;
                setStoreId(data.hashSiteId);
                setEcwidAuth({ store_id: storeId, access_token: token });
                const embedContext = {};
                if (data.lang != null) embedContext.lang = data.lang;
                if (data.view_mode != null) embedContext.view_mode = data.view_mode;
                if (data.public_token != null) embedContext.public_token = data.public_token;
                if (data.app_state != null) embedContext.app_state = data.app_state;
                setEcwidEmbedContext(embedContext);
                const embedLang = String(embedContext.lang || '').toLowerCase().split(/[-_]/)[0];
                if (SUPPORTED_LANGS.includes(embedLang)) {
                  i18n.changeLanguage(embedLang);
                }
                initializeSession();
                await loadApiKeyAndSettings(data.hashSiteId);
                setIsAuthenticated(true);
                setLoading(false);
                fetchStoreInfo(storeId, token);
                return;
              }
            } else {
              logger.warn("[App] embed-auth failed", res.status);
            }
          } else {
            logger.warn("[App] embed-auth skipped, no store credentials");
          }
        } catch (e) {
          logger.error("Embedded Ecwid auth failed:", e);
        }
        setLoading(false);
        return;
      }

      if (id) {
        // New flow: using hash ID from URL
        fetchStoreDataById(id).then(siteData => {
          if (siteData) {
            // Store hash_id in sessionStorage (non-sensitive)
            setStoreId(id);

            // Store sensitive data in sessionStorage (cleared when tab closes)
            setEcwidAuth({
              store_id: siteData.store_id,
              access_token: siteData.access_token
            });

            // Initialize session with timestamp
            initializeSession();

            // Load API key and settings from backend (use hashSiteId, not store_id)
            loadApiKeyAndSettings(id).then(() => {
              setIsAuthenticated(true);
              fetchStoreInfo(siteData.store_id, siteData.access_token);
            });
          } else {
            setLoading(false);
          }
        });
      } else if (storeId && token) {
        // Legacy flow: direct store_id and token from URL
        // Note: This flow doesn't have hashSiteId, so we can't load settings
        // Store sensitive data in sessionStorage
        setEcwidAuth({
          store_id: storeId,
          access_token: token
        });

        // Initialize session with timestamp
        initializeSession();

        // For legacy flow, we can't load settings without hashSiteId
        // User will need to go through OAuth flow again
        setIsAuthenticated(true);
        fetchStoreInfo(storeId, token);
      } else if (storedAuth && storedHashId) {
        // Use stored authentication data
        try {
          if (storedAuth.store_id && storedAuth.access_token) {
            // Load API key and settings from backend (use hashSiteId, not store_id)
            loadApiKeyAndSettings(storedHashId).then(() => {
              setIsAuthenticated(true);
              fetchStoreInfo(storedAuth.store_id, storedAuth.access_token);
            });
          } else {
            setLoading(false);
          }
        } catch (error) {
          logger.error("Failed to parse stored auth data:", error);
          clearExpiredSession();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  // Fetch store data by hashSiteId (renamed to avoid conflict with imported getStoreId)
  const fetchStoreDataById = async (id) => {
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/store-id?id=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.store_id && data.access_token) {
          return data;
        }
      }
      return null;
    } catch (error) {
      logger.error("Failed to fetch store data:", error);
      return null;
    }
  };

  const fetchStoreInfo = async (storeId, token) => {
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/store-info?storeId=${storeId}&token=${token}`);

      if (response.ok) {
        const data = await response.json();
        setStoreInfo(data);
      } else {
        const errorData = await response.json();
        logger.error("Store info API error:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
      }
    } catch (error) {
      logger.error("Failed to fetch store info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all stored data using utility function
    clearAllStorage();

    setIsAuthenticated(false);
    setStoreInfo(null);
    const base = (import.meta.env.VITE_BACKEND_URL || "").replace(/\/$/, "");
    window.location.href = base ? `${base}/install` : "/install";
  };

  return (
    <>
      <Toaster
        position="top-center"
        containerClassName="q2c-toaster"
        containerStyle={{
          zIndex: 2147483646,
          top: 'calc(var(--q2c-app-topbar-offset, 56px) + 8px)',
        }}
        toastOptions={{
          duration: 4000,
          className: 'q2c-toaster-item',
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
            border: 'none',
            maxWidth: 'none',
          },
        }}
      />
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("app.loadingStore")}</p>
        </div>
      ) : !isAuthenticated ? (
        <Login />
      ) : (
        <MainApp storeInfo={storeInfo} onLogout={handleLogout} />
      )}
    </>
  );
}

export default App;
